/**
 * The real post-apply bookkeeping, registered into the seam in `applyRecorder`.
 *
 * Runs once per undoable apply, in this order — the order matters:
 *
 *   1. **Claim the before-slot.** `enqueueBeforeMesh` asks the manifest whether
 *      the step is still untouched to decide between "store a blob" and "point at
 *      the previous step's checkpoint". That question must be asked *before* this
 *      apply is recorded, or the first tool on a step would look like the second
 *      and we would upload a mesh we already have.
 *   2. **Record the apply** in the manifest (order, duration, vertex count).
 *   3. **Record the operation** in the replay log, if the tool supplied params.
 *
 * Importing this module has the side effect of wiring the seam. It is pulled in
 * from the feature's `index.js`, alongside the replay handler registrations.
 */
import useWorkflowStore from '../../../shared/stores/useWorkflowStore';
import { isCheckpointStep } from '../constants';
import { setApplyRecorder } from './applyRecorder';
import useUndoManifestStore from './undoManifestStore';
import { enqueueBeforeMesh } from './beforeMeshUploader';
import { recordCurrentStepOperation } from '../operationLog/recordOperation';
import { calibrateDevice } from './transferRate';
import { needsBeforeMesh } from './undoStrategy';
import { scheduleStepCheckpointPush } from './checkpointAutoPush';

const LOG_PREFIX = '[ApplyRecorderImpl]';

function record({ toolId, durationMs, vertexCount, prePayload, params }) {
  const stepId = useWorkflowStore.getState().currentStep;
  if (!stepId || !isCheckpointStep(stepId)) return;
  if (!needsBeforeMesh(toolId)) return; // measurement-only tool, nothing to undo

  const manifest = useUndoManifestStore.getState();

  // 1. Before-slot first — see the ordering note above.
  if (prePayload?.geometry) {
    enqueueBeforeMesh(stepId, toolId, prePayload);
  }

  // 2. Apply record.
  manifest.recordApply(stepId, toolId, {
    durationMs: Number.isFinite(durationMs) ? durationMs : null,
    vertexCount: Number.isFinite(vertexCount) ? vertexCount : null,
  });

  // 3. Replay log. Only meaningful once a tool passes params through its command;
  // an empty-params entry still records order and timing, which the router uses.
  recordCurrentStepOperation(toolId, params ?? {}, stepId, {
    durationMs,
    vertexCount,
    deviceVerticesPerMs: calibrateDevice(),
  });

  // 4. Get the result onto the server. Debounced, so a burst of applies is one
  //    upload; without this the work lives only on this device until the user
  //    happens to navigate.
  scheduleStepCheckpointPush(stepId, 'apply');

  if (import.meta.env?.DEV) {
    console.log(
      `${LOG_PREFIX} ${stepId}/${toolId} ${Math.round(durationMs)}ms ` +
        `${vertexCount ?? '?'} verts`,
    );
  }
}

setApplyRecorder(record);

export { record as recordToolApply };
